/*
 * Keeps the localStorage of the author code running in scene, vibe-code and
 * blocks frames.
 *
 * Those frames are sandboxed with an opaque origin, where the browser's own
 * storage throws. The shim injected into every frame (scene-auth.js) gives
 * the page an in-memory localStorage, starts it from what SceneFrame hands
 * over, and posts every change back. The changes land here and are kept in
 * IndexedDB, one store per viewer, project and tab, so a game's high score or
 * a to-do list survives reloads and visits as it did before the sandbox.
 *
 * Limits: about 1M characters per store (the shim enforces the same, and
 * throws QuotaExceededError as the browser would), and about 25M characters
 * in all, beyond which the least recently used stores are dropped.
 */

export type StorageOp =
    | ['clear']
    | ['set', string, string]
    | ['remove', string];

export const STORE_QUOTA = 1_000_000;
const TOTAL_QUOTA = 25_000_000;
const PERSIST_DELAY_MS = 500;

const DB_NAME = 'mixiply-scene-storage';
const OBJECT_STORE = 'stores';

interface StoredRecord {
    key: string;
    items: Record<string, string>;
    size: number;
    usedAt: number;
}

export interface SceneStore {
    snapshot(): Record<string, string>;
    apply(ops: unknown): void;
    flush(): Promise<void>;
}

export const sceneStoreKey = (
    userId: string | null,
    projectId: string,
    dataIndex: number
) => `v1:${userId ?? 'guest'}:${projectId.toLowerCase()}:${dataIndex}`;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, 1);
            request.onupgradeneeded = () => {
                const store = request.result.createObjectStore(OBJECT_STORE, {
                    keyPath: 'key'
                });
                store.createIndex('usedAt', 'usedAt');
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
        dbPromise.catch(() => {
            dbPromise = null;
        });
    }
    return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function readRecord(key: string): Promise<StoredRecord | undefined> {
    const db = await openDb();
    return requestToPromise(
        db
            .transaction(OBJECT_STORE)
            .objectStore(OBJECT_STORE)
            .get(key) as IDBRequest<StoredRecord | undefined>
    );
}

async function writeRecord(record: StoredRecord) {
    const db = await openDb();
    await requestToPromise(
        db
            .transaction(OBJECT_STORE, 'readwrite')
            .objectStore(OBJECT_STORE)
            .put(record)
    );
}

// Drops the least recently used stores while the total is over TOTAL_QUOTA, never the one just written.
async function evict(keep: string) {
    const db = await openDb();
    const store = db
        .transaction(OBJECT_STORE, 'readwrite')
        .objectStore(OBJECT_STORE);
    const records = (await requestToPromise(store.getAll())) as StoredRecord[];
    let total = records.reduce((sum, record) => sum + record.size, 0);

    for (const record of records.sort((a, b) => a.usedAt - b.usedAt)) {
        if (total <= TOTAL_QUOTA) {
            break;
        }
        if (record.key !== keep) {
            store.delete(record.key);
            total -= record.size;
        }
    }
}

const sizeOf = (items: Map<string, string>) => {
    let size = 0;
    items.forEach((value, key) => (size += key.length + value.length));
    return size;
};

/*
 * Before the sandbox, frames ran on the app's own origin in production and
 * wrote straight into the app's localStorage. When a viewer's store for a tab
 * is first created, keys that the tab's own source names as string literals
 * are copied across from there (and left in place). Keys built at run time
 * can't be found this way. The app's own keys, and anything that looks like a
 * token, are never copied, whatever the source names.
 */
const APP_KEYS = new Set([
    'access_token',
    'refresh_token',
    'redir',
    'focus-mode-discovered',
    'complexityLevel'
]);

const looksLikeToken = (value: string) =>
    /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value) && value.length > 60;

function legacyItems(source: string): Map<string, string> {
    const items = new Map<string, string>();
    let legacy: Storage;
    try {
        legacy = window.localStorage;
        if (legacy.length === 0) {
            return items;
        }
    } catch {
        return items;
    }

    const literal = /(['"`])((?:\\.|(?!\1)[^\\\n]){1,200})\1/g;
    let size = 0;
    for (const match of source.matchAll(literal)) {
        const key = match[2];
        if (items.has(key) || APP_KEYS.has(key) || /token/i.test(key)) {
            continue;
        }
        const value = legacy.getItem(key);
        if (value === null || looksLikeToken(value)) {
            continue;
        }
        size += key.length + value.length;
        if (size > STORE_QUOTA) {
            break;
        }
        items.set(key, value);
    }
    return items;
}

function hasLegacyCandidates(): boolean {
    try {
        for (let i = 0; i < window.localStorage.length; i++) {
            const key = window.localStorage.key(i);
            if (key !== null && !APP_KEYS.has(key) && !/token/i.test(key)) {
                return true;
            }
        }
    } catch {
        /* no storage on the app's side either */
    }
    return false;
}

function isOp(op: unknown): op is StorageOp {
    if (!Array.isArray(op)) {
        return false;
    }
    switch (op[0]) {
        case 'clear':
            return op.length === 1;
        case 'set':
            return (
                op.length === 3 &&
                typeof op[1] === 'string' &&
                typeof op[2] === 'string'
            );
        case 'remove':
            return op.length === 2 && typeof op[1] === 'string';
        default:
            return false;
    }
}

function createStore(
    key: string,
    items: Map<string, string>,
    unsaved = false
): SceneStore {
    let size = sizeOf(items);
    let timer: ReturnType<typeof setTimeout> | null = null;
    let dirty = unsaved;

    const persist = async () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
        if (!dirty) {
            return;
        }
        dirty = false;
        try {
            await writeRecord({
                key,
                items: Object.fromEntries(items),
                size,
                usedAt: Date.now()
            });
            await evict(key);
        } catch {
            // Private browsing or storage blocked: the data lasts as long as the app is open.
        }
    };

    return {
        snapshot: () => Object.fromEntries(items),
        apply(ops: unknown) {
            if (!Array.isArray(ops)) {
                return;
            }
            for (const op of ops) {
                if (!isOp(op)) {
                    continue;
                }
                if (op[0] === 'clear') {
                    items.clear();
                    size = 0;
                } else if (op[0] === 'remove') {
                    const previous = items.get(op[1]);
                    if (previous !== undefined) {
                        items.delete(op[1]);
                        size -= op[1].length + previous.length;
                    }
                } else {
                    const [, itemKey, value] = op;
                    const previous = items.get(itemKey);
                    const next =
                        size -
                        (previous === undefined
                            ? 0
                            : itemKey.length + previous.length) +
                        itemKey.length +
                        value.length;
                    // The shim refuses the same writes, so this only stops a frame that bypasses it.
                    if (next > STORE_QUOTA) {
                        continue;
                    }
                    items.set(itemKey, value);
                    size = next;
                }
                dirty = true;
            }
            if (dirty && !timer) {
                timer = setTimeout(persist, PERSIST_DELAY_MS);
            }
        },
        flush: persist
    };
}

// One store per key while the app is open, shared by every frame showing that tab.
const openStores = new Map<string, Promise<SceneStore>>();

export function openSceneStore(
    key: string,
    loadLegacySource: () => Promise<string>
): Promise<SceneStore> {
    let store = openStores.get(key);
    if (!store) {
        store = (async () => {
            let record: StoredRecord | undefined;
            try {
                record = await readRecord(key);
            } catch {
                return createStore(key, new Map());
            }
            if (record) {
                return createStore(key, new Map(Object.entries(record.items)));
            }

            let initial = new Map<string, string>();
            if (hasLegacyCandidates()) {
                try {
                    initial = legacyItems(await loadLegacySource());
                } catch {
                    /* no source, no copy */
                }
            }
            // Written straight away so the copy happens once, even if the frame never writes.
            const created = createStore(key, initial, true);
            await created.flush();
            return created;
        })();
        openStores.set(key, store);
    }
    return store;
}

// Writes out anything still waiting as the app's page goes away (best effort: the page may be gone first).
window.addEventListener('pagehide', () =>
    openStores.forEach(store => store.then(s => s.flush()))
);
